/**
 * Helper para máscara e validação de contas bancárias brasileiras.
 *
 * - Máscaras (comprimento/placeholder) para agência, dígito da agência, conta
 *   e dígito da conta, por banco.
 * - Validação do dígito verificador (DV) da conta com algoritmos específicos
 *   para os bancos mais comuns, e fallback para Módulo 11 padrão.
 *
 * A detecção do banco é feita pelo texto (description) do meio de pagamento,
 * exigindo que contenha "Débito em conta" seguido do nome do banco (matching
 * case-insensitive).
 *
 * Caso o banco não seja reconhecido, `resolveBankMaskConfig` retorna `null`
 * e a tela cai no comportamento padrão (lengths máximos genéricos + regex do
 * backend para validação de DV).
 */

export type BankKey =
  | 'bb'
  | 'santander'
  | 'bradesco'
  | 'itau'
  | 'caixa'
  | 'safra'
  | 'nubank'
  | 'c6'
  | 'inter'
  | 'btg'
  | 'citibank'
  | 'abc'
  | 'nordeste'
  | 'banrisul'
  | 'genial';

export type BankMaskConfig = {
  key: BankKey;
  label: string;
  agencyLength: number;
  agencyPlaceholder: string;
  agencyDigitEnabled: boolean;
  agencyDigitLength: number;
  agencyDigitPlaceholder: string;
  accountLength: number;
  accountPlaceholder: string;
  accountDigitLength: number;
  accountDigitPlaceholder: string;
};

/** Placeholder helper (0000, 00000...) */
const zeroes = (n: number): string => (n > 0 ? '0'.repeat(n) : '');

/**
 * Configurações de máscara por banco. Para agências fixas em bancos digitais
 * (ex: Nubank = 0001), mantemos o comprimento em 4 — o próprio backend pode
 * pré-preencher a agência, mas o usuário continua podendo editar.
 */
export const BANK_MASK_CONFIGS: Record<BankKey, BankMaskConfig> = {
  bb: {
    key: 'bb',
    label: 'Banco do Brasil',
    agencyLength: 4,
    agencyPlaceholder: zeroes(4),
    agencyDigitEnabled: true,
    agencyDigitLength: 1,
    agencyDigitPlaceholder: 'X',
    accountLength: 8,
    accountPlaceholder: zeroes(8),
    accountDigitLength: 1,
    accountDigitPlaceholder: 'X',
  },
  santander: {
    key: 'santander',
    label: 'Santander',
    agencyLength: 4,
    agencyPlaceholder: zeroes(4),
    agencyDigitEnabled: false,
    agencyDigitLength: 0,
    agencyDigitPlaceholder: '',
    accountLength: 8,
    accountPlaceholder: zeroes(8),
    accountDigitLength: 1,
    accountDigitPlaceholder: '0',
  },
  bradesco: {
    key: 'bradesco',
    label: 'Bradesco',
    agencyLength: 4,
    agencyPlaceholder: zeroes(4),
    agencyDigitEnabled: true,
    agencyDigitLength: 1,
    agencyDigitPlaceholder: 'X',
    accountLength: 7,
    accountPlaceholder: zeroes(7),
    accountDigitLength: 1,
    accountDigitPlaceholder: 'X',
  },
  itau: {
    key: 'itau',
    label: 'Itaú',
    agencyLength: 4,
    agencyPlaceholder: zeroes(4),
    agencyDigitEnabled: false,
    agencyDigitLength: 0,
    agencyDigitPlaceholder: '',
    accountLength: 5,
    accountPlaceholder: zeroes(5),
    accountDigitLength: 1,
    accountDigitPlaceholder: '0',
  },
  caixa: {
    // Conta Caixa costuma ter 11 dígitos: 3 de operação (ex: 001 CC, 013 Poupança)
    // + 8 da conta. O usuário digita tudo no campo "Conta".
    key: 'caixa',
    label: 'Caixa Econômica Federal',
    agencyLength: 4,
    agencyPlaceholder: zeroes(4),
    agencyDigitEnabled: false,
    agencyDigitLength: 0,
    agencyDigitPlaceholder: '',
    accountLength: 11,
    accountPlaceholder: zeroes(11),
    accountDigitLength: 1,
    accountDigitPlaceholder: '0',
  },
  safra: {
    key: 'safra',
    label: 'Safra',
    agencyLength: 5,
    agencyPlaceholder: zeroes(5),
    agencyDigitEnabled: true,
    agencyDigitLength: 1,
    agencyDigitPlaceholder: '0',
    accountLength: 9,
    accountPlaceholder: zeroes(9),
    accountDigitLength: 1,
    accountDigitPlaceholder: 'X',
  },
  nubank: {
    key: 'nubank',
    label: 'Nubank',
    agencyLength: 4,
    agencyPlaceholder: '0001',
    agencyDigitEnabled: false,
    agencyDigitLength: 0,
    agencyDigitPlaceholder: '',
    accountLength: 10,
    accountPlaceholder: zeroes(10),
    accountDigitLength: 1,
    accountDigitPlaceholder: '0',
  },
  c6: {
    key: 'c6',
    label: 'C6 Bank',
    agencyLength: 4,
    agencyPlaceholder: '0001',
    agencyDigitEnabled: false,
    agencyDigitLength: 0,
    agencyDigitPlaceholder: '',
    accountLength: 10,
    accountPlaceholder: zeroes(10),
    accountDigitLength: 1,
    accountDigitPlaceholder: '0',
  },
  inter: {
    key: 'inter',
    label: 'Inter',
    agencyLength: 4,
    agencyPlaceholder: '0001',
    agencyDigitEnabled: false,
    agencyDigitLength: 0,
    agencyDigitPlaceholder: '',
    accountLength: 10,
    accountPlaceholder: zeroes(10),
    accountDigitLength: 1,
    accountDigitPlaceholder: '0',
  },
  btg: {
    key: 'btg',
    label: 'BTG Pactual',
    agencyLength: 4,
    agencyPlaceholder: '0050',
    agencyDigitEnabled: false,
    agencyDigitLength: 0,
    agencyDigitPlaceholder: '',
    accountLength: 10,
    accountPlaceholder: zeroes(10),
    accountDigitLength: 1,
    accountDigitPlaceholder: '0',
  },
  citibank: {
    key: 'citibank',
    label: 'Citibank',
    agencyLength: 4,
    agencyPlaceholder: zeroes(4),
    agencyDigitEnabled: true,
    agencyDigitLength: 1,
    agencyDigitPlaceholder: '0',
    accountLength: 10,
    accountPlaceholder: zeroes(10),
    accountDigitLength: 1,
    accountDigitPlaceholder: '0',
  },
  abc: {
    key: 'abc',
    label: 'ABC Brasil',
    agencyLength: 4,
    agencyPlaceholder: zeroes(4),
    agencyDigitEnabled: true,
    agencyDigitLength: 1,
    agencyDigitPlaceholder: '0',
    accountLength: 9,
    accountPlaceholder: zeroes(9),
    accountDigitLength: 1,
    accountDigitPlaceholder: '0',
  },
  nordeste: {
    key: 'nordeste',
    label: 'Banco do Nordeste',
    agencyLength: 4,
    agencyPlaceholder: zeroes(4),
    agencyDigitEnabled: true,
    agencyDigitLength: 1,
    agencyDigitPlaceholder: '0',
    accountLength: 7,
    accountPlaceholder: zeroes(7),
    accountDigitLength: 1,
    accountDigitPlaceholder: '0',
  },
  banrisul: {
    key: 'banrisul',
    label: 'Banrisul',
    agencyLength: 4,
    agencyPlaceholder: zeroes(4),
    agencyDigitEnabled: true,
    agencyDigitLength: 2,
    agencyDigitPlaceholder: '00',
    accountLength: 9,
    accountPlaceholder: zeroes(9),
    accountDigitLength: 2,
    accountDigitPlaceholder: '00',
  },
  genial: {
    key: 'genial',
    label: 'Genial',
    agencyLength: 4,
    agencyPlaceholder: '0001',
    agencyDigitEnabled: false,
    agencyDigitLength: 0,
    agencyDigitPlaceholder: '',
    accountLength: 10,
    accountPlaceholder: zeroes(10),
    accountDigitLength: 1,
    accountDigitPlaceholder: '0',
  },
};

/**
 * Dicionário de sinônimos/labels para matching. Cada chave aceita variações
 * frequentes usadas em DataTable (ex: "Débito em conta - Itaú Unibanco").
 */
const BANK_ALIASES: { key: BankKey; patterns: RegExp[] }[] = [
  { key: 'bb', patterns: [/banco do brasil/, /\bbb\b/] },
  { key: 'santander', patterns: [/santander/] },
  { key: 'bradesco', patterns: [/bradesco/] },
  { key: 'itau', patterns: [/ita[uú]/] },
  { key: 'caixa', patterns: [/caixa( econ[oô]mica)?/, /\bcef\b/] },
  { key: 'safra', patterns: [/safra/] },
  { key: 'nubank', patterns: [/nubank/, /\bnu\b/] },
  { key: 'c6', patterns: [/\bc6( bank)?\b/] },
  { key: 'inter', patterns: [/\binter\b/, /banco inter/] },
  { key: 'btg', patterns: [/\bbtg( pactual)?\b/] },
  { key: 'citibank', patterns: [/citi(bank)?/] },
  { key: 'abc', patterns: [/\babc( brasil)?\b/] },
  { key: 'nordeste', patterns: [/banco do nordeste/, /\bbnb\b/] },
  { key: 'banrisul', patterns: [/banrisul/] },
  { key: 'genial', patterns: [/genial/] },
];

/**
 * Identifica o banco a partir da descrição do meio de pagamento.
 * Exige que a descrição contenha "débito" (com ou sem acento) para evitar
 * falsos positivos em outras formas de pagamento.
 */
export function resolveBankKey(description?: string): BankKey | null {
  const desc = (description || '').toLowerCase();
  if (!desc.includes('débito') && !desc.includes('debito')) return null;

  for (const alias of BANK_ALIASES) {
    if (alias.patterns.some(p => p.test(desc))) return alias.key;
  }

  return null;
}

export function resolveBankMaskConfig(
  description?: string
): BankMaskConfig | null {
  const key = resolveBankKey(description);
  return key ? BANK_MASK_CONFIGS[key] : null;
}

// ---------------------------------------------------------------------------
// Validação do Dígito Verificador (DV) da agência por banco
// ---------------------------------------------------------------------------

/**
 * Valida o DV da agência conforme o algoritmo do banco.
 *
 * Retorno:
 * - `true`  → DV confere.
 * - `false` → DV não confere.
 * - `null`  → banco não aplica DV de agência, ou faltam dados para validar.
 */
export function validateBankAgencyDigit(
  bankKey: BankKey,
  agency: string,
  agencyDigit: string
): boolean | null {
  const cleanAgency = (agency || '').replace(/\D/g, '');
  const cleanDigit = (agencyDigit || '').toUpperCase();

  if (!cleanAgency) return null;

  const config = BANK_MASK_CONFIGS[bankKey];
  // Bancos sem DV de agência: se veio dígito, já é inválido; se veio vazio,
  // nada a validar.
  if (!config.agencyDigitEnabled) {
    return cleanDigit === '' ? null : false;
  }

  if (!cleanDigit) return null;

  switch (bankKey) {
    case 'bb':
      return validateAgencyDvBancoDoBrasil(cleanAgency, cleanDigit);
    case 'bradesco':
      return validateAgencyDvBradesco(cleanAgency, cleanDigit);
    // Bancos sem algoritmo público consolidado: aplica Módulo 11 padrão como
    // heurística. Se falhar, o regex do backend (`accountValidationRules`)
    // ainda valida o formato.
    case 'safra':
    case 'citibank':
    case 'abc':
    case 'nordeste':
    case 'banrisul':
      return validateAgencyDvMod11Generic(cleanAgency, cleanDigit);
    default:
      return null;
  }
}

/**
 * Banco do Brasil — DV da agência pelo Módulo 11 sobre 4 dígitos com pesos
 * 5, 4, 3, 2 (esquerda para direita).
 * Resto 0 → DV '0'; resto 1 → DV 'X'; senão DV = 11 - resto.
 */
function validateAgencyDvBancoDoBrasil(
  agency: string,
  digit: string
): boolean {
  if (!/^\d+$/.test(agency)) return false;
  const padded = agency.padStart(4, '0').slice(-4);
  const weights = [5, 4, 3, 2];
  const sum = padded
    .split('')
    .reduce((acc, d, i) => acc + Number(d) * weights[i], 0);
  const remainder = sum % 11;
  const expected =
    remainder === 0 ? '0' : remainder === 1 ? 'X' : String(11 - remainder);
  return expected === digit;
}

/**
 * Bradesco — DV da agência pelo Módulo 11 sobre 4 dígitos com pesos 2..5
 * da direita para esquerda.
 * DV 11 → '0'; DV 10 → 'P'.
 */
function validateAgencyDvBradesco(agency: string, digit: string): boolean {
  if (!/^\d+$/.test(agency)) return false;
  const padded = agency.padStart(4, '0').slice(-4);
  const weights = [2, 3, 4, 5];
  const sum = padded
    .split('')
    .reverse()
    .reduce((acc, d, i) => acc + Number(d) * weights[i], 0);
  const remainder = sum % 11;
  const computed = 11 - remainder;
  const expected =
    computed === 11 ? '0' : computed === 10 ? 'P' : String(computed);
  return expected === digit;
}

/**
 * Módulo 11 genérico para DV de agência (pesos 2..9 cíclicos da direita
 * para esquerda). Fallback para bancos sem algoritmo oficial consolidado.
 * Aceita '0' ou 'X' quando o DV calculado seria 10.
 */
function validateAgencyDvMod11Generic(agency: string, digit: string): boolean {
  if (!/^\d+$/.test(agency)) return false;
  const weights = [2, 3, 4, 5, 6, 7, 8, 9];
  const reversed = agency.split('').reverse();
  let sum = 0;
  for (let i = 0; i < reversed.length; i++) {
    sum += Number(reversed[i]) * weights[i % weights.length];
  }
  const remainder = sum % 11;
  const computed = 11 - remainder;
  const expectedCandidates: string[] = [];
  if (remainder < 2) expectedCandidates.push('0');
  else if (computed === 10) expectedCandidates.push('0', 'X');
  else expectedCandidates.push(String(computed));
  return expectedCandidates.includes(digit.toUpperCase());
}

// ---------------------------------------------------------------------------
// Validação do Dígito Verificador (DV) da conta por banco
// ---------------------------------------------------------------------------

/**
 * Valida o DV da conta com base no banco selecionado.
 *
 * Retorno:
 * - `true`  → DV confere com o algoritmo do banco.
 * - `false` → DV não confere.
 * - `null`  → banco/entrada não suficientes para validar (deixa a decisão
 *             para a validação baseada em regex vinda do backend).
 */
export function validateBankAccountDigit(
  bankKey: BankKey,
  agency: string,
  account: string,
  accountDigit: string
): boolean | null {
  const cleanAgency = (agency || '').replace(/\D/g, '');
  const cleanAccount = (account || '').replace(/\D/g, '');
  const cleanDigit = (accountDigit || '').toUpperCase();

  if (!cleanAccount || !cleanDigit) return null;

  switch (bankKey) {
    case 'bb':
      return validateDvBancoDoBrasil(cleanAccount, cleanDigit);
    case 'bradesco':
      return validateDvBradesco(cleanAccount, cleanDigit);
    case 'santander':
      return validateDvSantander(cleanAgency, cleanAccount, cleanDigit);
    case 'itau':
      return validateDvItau(cleanAgency, cleanAccount, cleanDigit);
    case 'caixa':
      return validateDvCaixa(cleanAccount, cleanDigit);
    // Bancos sem algoritmo público consolidado: aplicamos Módulo 11 padrão
    // como validação de consistência. Pode gerar falsos negativos em casos
    // raros — por isso mantemos o regex do backend (`accountValidationRules`)
    // como segunda linha de validação.
    case 'safra':
    case 'nubank':
    case 'c6':
    case 'inter':
    case 'btg':
    case 'citibank':
    case 'abc':
    case 'nordeste':
    case 'banrisul':
    case 'genial':
      return validateDvMod11Generic(cleanAccount, cleanDigit);
  }
}

/**
 * Banco do Brasil — Módulo 11, pesos 9..2 aplicados da esquerda para direita
 * sobre a conta (padded para 8 dígitos).
 * Resto 0 → DV '0'; resto 1 → DV 'X'; senão DV = 11 - resto.
 */
function validateDvBancoDoBrasil(account: string, digit: string): boolean {
  if (!/^\d+$/.test(account)) return false;
  const padded = account.padStart(8, '0').slice(-8);
  const weights = [9, 8, 7, 6, 5, 4, 3, 2];
  const sum = padded
    .split('')
    .reduce((acc, d, i) => acc + Number(d) * weights[i], 0);
  const remainder = sum % 11;
  const expected =
    remainder === 0 ? '0' : remainder === 1 ? 'X' : String(11 - remainder);
  return expected === digit;
}

/**
 * Bradesco — Módulo 11 com pesos 2..7 da direita para esquerda.
 * DV 10 → 'P'; DV 11 → '0'.
 */
function validateDvBradesco(account: string, digit: string): boolean {
  if (!/^\d+$/.test(account)) return false;
  const padded = account.padStart(7, '0').slice(-7);
  const weights = [2, 3, 4, 5, 6, 7];
  const sum = padded
    .split('')
    .reverse()
    .reduce((acc, d, i) => acc + Number(d) * weights[i % weights.length], 0);
  const remainder = sum % 11;
  const computed = 11 - remainder;
  const expected =
    computed === 11 ? '0' : computed === 10 ? 'P' : String(computed);
  return expected === digit;
}

/**
 * Itaú — Módulo 10 aplicado sobre agência(4) + conta(5), com pesos alternados
 * 2,1 da esquerda para direita. Produtos > 9 são somados dígito a dígito.
 * DV = (10 - resto) mod 10.
 */
function validateDvItau(
  agency: string,
  account: string,
  digit: string
): boolean {
  if (!/^\d+$/.test(account) || !/^\d+$/.test(agency)) return false;
  if (!/^\d$/.test(digit)) return false;
  const paddedAgency = agency.padStart(4, '0').slice(-4);
  const paddedAccount = account.padStart(5, '0').slice(-5);
  const base = `${paddedAgency}${paddedAccount}`;
  let sum = 0;
  for (let i = 0; i < base.length; i++) {
    const weight = i % 2 === 0 ? 2 : 1;
    let product = Number(base[i]) * weight;
    if (product > 9) product = Math.floor(product / 10) + (product % 10);
    sum += product;
  }
  const expected = (10 - (sum % 10)) % 10;
  return String(expected) === digit;
}

/**
 * Santander — Algoritmo oficial sobre agência(4) + "00" + conta(8).
 * Pesos: [9,7,3,1,0,0,9,7,1,3,1,9,7,3]. Soma o último dígito de cada produto.
 * DV = (10 - soma%10) mod 10.
 */
function validateDvSantander(
  agency: string,
  account: string,
  digit: string
): boolean {
  if (!/^\d+$/.test(account) || !/^\d+$/.test(agency)) return false;
  if (!/^\d$/.test(digit)) return false;
  const paddedAgency = agency.padStart(4, '0').slice(-4);
  const paddedAccount = account.padStart(8, '0').slice(-8);
  const base = `${paddedAgency}00${paddedAccount}`;
  const weights = [9, 7, 3, 1, 0, 0, 9, 7, 1, 3, 1, 9, 7, 3];
  let sum = 0;
  for (let i = 0; i < base.length; i++) {
    sum += (Number(base[i]) * weights[i]) % 10;
  }
  const expected = (10 - (sum % 10)) % 10;
  return String(expected) === digit;
}

/**
 * Caixa Econômica Federal — Módulo 11 sobre operação(3) + conta(8), total 11
 * posições com pesos 2..9 cíclicos da direita para esquerda.
 * Resto 0 ou 1 → DV '0'; senão DV = 11 - resto.
 */
function validateDvCaixa(account: string, digit: string): boolean {
  if (!/^\d+$/.test(account)) return false;
  if (!/^\d$/.test(digit)) return false;
  const padded = account.padStart(11, '0').slice(-11);
  const weights = [2, 3, 4, 5, 6, 7, 8, 9];
  let sum = 0;
  const reversed = padded.split('').reverse();
  for (let i = 0; i < reversed.length; i++) {
    sum += Number(reversed[i]) * weights[i % weights.length];
  }
  const remainder = sum % 11;
  const expected = remainder < 2 ? 0 : 11 - remainder;
  return String(expected) === digit;
}

/**
 * Módulo 11 genérico (pesos 2..9 cíclicos da direita para esquerda).
 * Fallback para bancos sem algoritmo oficial consolidado no helper.
 * Resto 0 ou 1 → DV '0'; resto == ? especial: aceita também 'X' quando
 * o DV esperado seria 10 (compatibilidade com bancos que usam letra).
 */
function validateDvMod11Generic(account: string, digit: string): boolean {
  if (!/^\d+$/.test(account)) return false;
  const weights = [2, 3, 4, 5, 6, 7, 8, 9];
  const reversed = account.split('').reverse();
  let sum = 0;
  for (let i = 0; i < reversed.length; i++) {
    sum += Number(reversed[i]) * weights[i % weights.length];
  }
  const remainder = sum % 11;
  const computed = 11 - remainder;
  const expectedCandidates: string[] = [];
  if (remainder < 2) expectedCandidates.push('0');
  else if (computed === 10) expectedCandidates.push('0', 'X');
  else expectedCandidates.push(String(computed));
  return expectedCandidates.includes(digit.toUpperCase());
}
